import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Copy, LogOut, ShieldCheck, Sparkles, Terminal, UserRound, Wallet } from 'lucide-react';
import { STORAGE_KEY } from './portalSessionKeys';

function safeJsonParse(raw) {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function readSession() {
  if (typeof window === 'undefined') return null;
  return safeJsonParse(window.localStorage.getItem(STORAGE_KEY));
}

function shortId(id, head = 6, tail = 4) {
  const v = String(id || '');
  if (v.length <= head + tail + 1) return v;
  return `${v.slice(0, head)}…${v.slice(-tail)}`;
}

function initials(email) {
  const e = String(email || '').trim();
  if (!e) return '·';
  const first = e[0];
  return first.toUpperCase();
}

function engineHost(raw) {
  const v = String(raw || '').trim();
  if (!v) return '';
  try {
    return new URL(v).host;
  } catch {
    return v.replace(/^https?:\/\//, '').replace(/\/.*$/, '');
  }
}

function navLinkClass(active) {
  const base =
    'text-[11px] px-3 py-1.5 rounded-lg border transition-all whitespace-nowrap font-semibold tracking-wide';
  if (active) {
    return `${base} text-white bg-gradient-to-r from-violet-600 to-cyan-500 border-transparent shadow-[0_0_18px_-6px_rgba(139,92,246,0.7)]`;
  }
  return `${base} text-slate-300 border-slate-700/70 hover:text-white hover:border-violet-500/60 hover:bg-slate-900/60`;
}

export default function DashboardNav() {
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  const norm = path.replace(/\/$/, '') || '/';

  const [session, setSession] = useState(() => readSession());
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const sync = () => setSession(readSession());
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, []);

  useEffect(() => {
    if (!profileOpen) return undefined;
    const onClick = (e) => {
      if (!profileRef.current) return;
      if (!profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [profileOpen]);

  const me = session?.me || null;
  const email = me?.email || '';
  const ownerId = session?.ownerId || me?.owner_id || '';
  const role = me?.role || 'user';

  const copyOwner = () => {
    if (typeof navigator === 'undefined' || !navigator.clipboard) return;
    void navigator.clipboard.writeText(ownerId);
  };

  const isOverview = norm === '/';
  const isAgents = norm.includes('/account-hub/agents');
  const isAppIntegrations =
    norm.startsWith('/account-hub') &&
    !norm.includes('/account-hub/accounts') &&
    !norm.includes('/account-hub/wallets') &&
    !isAgents;
  const isLinkedAccounts = norm.includes('/account-hub/accounts');
  const isWallets = norm.includes('/account-hub/wallets');

  const logout = () => {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.removeItem(STORAGE_KEY);
    window.localStorage.removeItem('apiKeyPortalSession');
    window.localStorage.removeItem('apiKeyPortalEngineUrl');
    window.sessionStorage.clear();
    window.location.href = '/auth/login';
  };

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-2 justify-end">
      <a href="/" className={navLinkClass(isOverview)}>
        Overview
      </a>
      <a href="/account-hub/agents" className={navLinkClass(isAgents)}>
        Agents
      </a>
      <a href="/account-hub/apps" className={navLinkClass(isAppIntegrations)}>
        App integrations
      </a>
      <a href="/account-hub/accounts" className={navLinkClass(isLinkedAccounts)}>
        Linked accounts
      </a>
      <a href="/account-hub/wallets" className={navLinkClass(isWallets)}>
        Wallets
      </a>

      <div className="relative" ref={profileRef}>
        <button
          type="button"
          onClick={() => setProfileOpen((v) => !v)}
          className={`flex items-center gap-2 rounded-full pl-1 pr-3 py-1 border transition-all ${
            profileOpen
              ? 'border-violet-500/60 bg-slate-900 shadow-[0_0_18px_-6px_rgba(139,92,246,0.7)]'
              : 'border-slate-700/70 bg-slate-900/70 hover:border-violet-500/60 hover:bg-slate-900'
          }`}
          aria-haspopup="menu"
          aria-expanded={profileOpen}
        >
          <span className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 via-fuchsia-500 to-cyan-400 flex items-center justify-center text-[12px] font-bold text-white shadow-inner">
            {initials(email)}
          </span>
          <span className="hidden sm:flex flex-col leading-tight text-left">
            <span className="text-[10px] uppercase tracking-wider text-slate-500">Signed in</span>
            <span className="text-[11px] text-slate-100 font-semibold max-w-[150px] truncate">
              {email || 'Anonymous session'}
            </span>
          </span>
          <ChevronDown size={12} className={`text-slate-400 transition-transform ${profileOpen ? 'rotate-180' : ''}`} />
        </button>

        {profileOpen && (
          <div
            role="menu"
            className="absolute right-0 mt-2 w-80 rounded-2xl border border-slate-700/80 bg-gradient-to-b from-slate-900 to-slate-950 shadow-2xl p-4 z-50 backdrop-blur"
          >
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-full bg-gradient-to-br from-violet-500 via-fuchsia-500 to-cyan-400 flex items-center justify-center text-base font-bold text-white shadow-inner">
                {initials(email)}
              </span>
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-wider text-slate-500">Profile</p>
                <p className="text-sm font-semibold text-slate-100 truncate" title={email}>
                  {email || 'Local session'}
                </p>
              </div>
            </div>
            <dl className="mt-4 grid grid-cols-1 gap-2 text-[11px]">
              <div className="flex items-center justify-between gap-2 rounded-lg border border-slate-800 bg-slate-950/80 px-3 py-2">
                <dt className="text-slate-500 inline-flex items-center gap-1.5">
                  <ShieldCheck size={12} /> Role
                </dt>
                <dd className="text-slate-200 font-medium uppercase tracking-wider">{role}</dd>
              </div>
              <div className="flex items-center justify-between gap-2 rounded-lg border border-slate-800 bg-slate-950/80 px-3 py-2">
                <dt className="text-slate-500 inline-flex items-center gap-1.5">
                  <UserRound size={12} /> Owner id
                </dt>
                <dd className="flex items-center gap-1.5">
                  <code className="text-cyan-200/90">{shortId(ownerId)}</code>
                  {ownerId ? (
                    <button
                      type="button"
                      onClick={copyOwner}
                      className="text-slate-500 hover:text-cyan-300"
                      aria-label="Copy owner id"
                    >
                      <Copy size={12} />
                    </button>
                  ) : null}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-2 rounded-lg border border-slate-800 bg-slate-950/80 px-3 py-2">
                <dt className="text-slate-500 inline-flex items-center gap-1.5">
                  <Sparkles size={12} /> Auth
                </dt>
                <dd className="text-slate-200 font-medium uppercase tracking-wider">
                  {session?.authMode || 'jwt'}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-2 rounded-lg border border-slate-800 bg-slate-950/80 px-3 py-2">
                <dt className="text-slate-500 inline-flex items-center gap-1.5">
                  <Wallet size={12} /> Engine
                </dt>
                <dd
                  className="text-slate-200 text-[10px] truncate max-w-[160px]"
                  title={session?.engineUrl || ''}
                >
                  {engineHost(session?.engineUrl) || '—'}
                </dd>
              </div>
            </dl>
            <button
              type="button"
              onClick={logout}
              className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-lg py-2 text-xs font-semibold bg-gradient-to-r from-rose-500 via-pink-500 to-fuchsia-500 hover:opacity-90 text-white shadow-lg"
            >
              <LogOut size={14} /> Log out
            </button>
          </div>
        )}
      </div>

      <div className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1">
        <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" aria-hidden />
        <span className="text-[10px] uppercase tracking-wider text-emerald-300 font-semibold">Engine live</span>
      </div>
    </div>
  );
}

export function DashboardBrandRow() {
  return (
    <div className="flex items-center gap-3 min-w-0">
      <span className="relative shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-xl bg-gradient-to-br from-violet-600 via-fuchsia-500 to-cyan-400 shadow-[0_0_25px_-5px_rgba(139,92,246,0.6)]">
        <Terminal className="text-white" size={18} />
      </span>
      <div className="min-w-0">
        <h1 className="text-base sm:text-lg font-bold tracking-tighter uppercase truncate bg-gradient-to-r from-violet-200 via-fuchsia-200 to-cyan-200 bg-clip-text text-transparent">
          AI Execution Gateway
        </h1>
        <p className="text-[10px] uppercase tracking-[0.3em] text-slate-500">Autonomous routing · v1.0</p>
      </div>
    </div>
  );
}
